import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiRequest } from '../utils/apiRequest';
import { globalEndpoints } from '../globals/globalEndpoints';
import { useStudentContext } from './StudentContextProvider';
import useAnalyticsEvents from '../hooks/useAnalyticsEvents';

interface ITimegateContext {
    isTimegated: boolean;
    checkedTimegate: boolean;
    gameName: string | undefined;
    setGameName: (gameName: string | undefined) => void;
    tryCheckTimegate: () => void;
}

const TimegateContext = createContext<ITimegateContext>({
    isTimegated: false,
    checkedTimegate: false,
    gameName: undefined,
    setGameName: () => {},
    tryCheckTimegate: () => {}
});

export const useTimegateContext = () => {
    const { isTimegated, checkedTimegate, gameName, setGameName, tryCheckTimegate } = useContext(TimegateContext);
    return { isTimegated, checkedTimegate, gameName, setGameName, tryCheckTimegate };
}

const TimegateContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [isTimegated, setIsTimegated] = useState<boolean>(false);
    const [checkedTimegate, setCheckedTimegate] = useState<boolean>(false);
    const [waitingForTimegateResponse, setWaitingForTimegateResponse] = useState<boolean>(false);
    const [gameName, setGameNameInternal] = useState<string | undefined>(undefined);
    const { studentCode, student } = useStudentContext()
    const analyticsEvents = useAnalyticsEvents();

    const setGameName = (newGameName: string | undefined) => {
        if (!newGameName) {
            return;
        }
        setGameNameInternal(newGameName);
    }

    useEffect(() => {
        if (studentCode && !checkedTimegate) {
            tryCheckTimegate();
        }
    }, [studentCode])

    useEffect(() => {
        if (gameName) {
            tryCheckTimegate();
        }
    }, [gameName])

    const tryCheckTimegate = () => {
        if (waitingForTimegateResponse)
        {
            return;
        }

        // No timegate for parent accounts, paywalls must be shown in Unity once a student has been selected
        if (studentCode == 'n' || window.location.search.includes('teacher-demo'))
        {
            setIsTimegated(false);
            setCheckedTimegate(true);
            return;
        }

        if (!studentCode || !gameName)
        {
            return;
        }

        setWaitingForTimegateResponse(true);
        apiRequest(globalEndpoints.timegate.check(studentCode, formatDateTimeForRuby(), gameName), 'GET')
            .then(async(res) => {
                if (res.ok) {
                    const data = await res.json();
                    setWaitingForTimegateResponse(false);
                    const wasTimegated = isTimegated;
                    setIsTimegated(data[`is_timegated`]);
                    setCheckedTimegate(true);
                    if (data[`is_timegated`] && !wasTimegated)
                    {
                        handleViewedPaywallEvent();
                    }
                    return;
                }
                const error = await res.json();
                setWaitingForTimegateResponse(false);
    
                throw new Error(error?.message ? error.message : 'Could not check if user is timegated');
            })
    }

    const handleViewedPaywallEvent = () => {
        if (!student) return;
        analyticsEvents.trackEvent({
            event_name: 'Viewed Paywall',
            user_id: student.id,
            user_type: 'student',
            analytics_services: ['amplitude'],
            event_metadata: {
                'Paywall ID': "Timegate",
                'Game' : gameName
            }
        })
    }

    function formatDateTimeForRuby(): string {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        
        const now = new Date();
        
        const dayOfMonth = now.getDate().toString().padStart(2, '0');
        const month = months[now.getMonth()];
        const year = now.getFullYear();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const seconds = now.getSeconds().toString().padStart(2, '0');
        
        return `${dayOfMonth} ${month} ${year} ${hours}:${minutes}:${seconds}`;
    }

    return (
        <TimegateContext.Provider value={{ isTimegated, checkedTimegate, gameName, setGameName, tryCheckTimegate }}>
            {children}
        </TimegateContext.Provider>
    );
}

export default TimegateContextProvider;